import {
  WSButton,
  WSText,
  WSIconList,
  toWSMoneyString,
  WSProgressBar,
  WSPanel,
  WSFlexBox,
  WSLoader,
  WSForm,
  WSRadioInputGroup
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useState } from "react";
import qs from "qs";
import { useHistory } from "react-router-dom";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { UploadPayable } from "../../Invoices/screens/payables/bulkUploadPayables/BulkUploadPayablesInfo";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";

import styles from "./styles.module.scss";
import { bulkActionFactory } from "../../utils/bulkActionFactory";
import {
  BulkStatus,
  IBulkCollaboratorBatch,
  IBulkPayableBatch,
  IBulkPayableItem,
  IBulkPayableItemCreate,
  PayableStatus
} from "@wingspanhq/payments/dist/interfaces";
import { paymentsService } from "../../services/payments";
import { useUpdateBulkPayableBatch } from "../../query/payments/mutations";
import {
  useBulkPayableBatchItems,
  useBulkPayableBatch,
  useBulkPayableBatchItemsNotYetProcessed
} from "../../query/payments/queries";
import { getTotalLineItemsAmountFromCSV } from "../../Invoices/screens/payables/bulkUploadPayables/bulkUploadPayablesUtils";
import { useUserId } from "../../query/hooks/helpers";
import { useActivities } from "../../query/users/queries";
import { get1099SetupProgress } from "../../query/users/selectors";
import { CSVLink } from "react-csv";
import { AxiosError } from "axios";
import { isEmpty } from "lodash";
import { PreventLeave } from "../../components/PreventLeave/PreventLeave";
import { useUpdateActivity } from "../../query/users/mutations";

export const ignoreDeductions = (lineItems: UploadPayable[]) => {
  return lineItems.filter(lineItem => {
    const amount = Number(lineItem.amount?.replaceAll(",", ""));
    return amount >= 0;
  });
};

export const lineItemsToBulkBatchItems = (
  lineItems: UploadPayable[],
  bulkPayableBatchId: string,
  payableStatus: PayableStatus
): IBulkPayableItemCreate[] => {
  return lineItems.map(lineItem => {
    return {
      bulkPayableBatchId,
      ...(lineItem.email ? { collaboratorEmail: lineItem.email } : {}),
      ...(lineItem.collaboratorId
        ? { collaboratorId: lineItem.collaboratorId }
        : {}),
      ...(lineItem.contractorId
        ? { collaboratorExternalId: lineItem.contractorId }
        : {}),
      payableStatus: payableStatus || PayableStatus.Paid,
      amount: Number(lineItem.amount?.replaceAll(",", "")),
      lineItemDescription: lineItem.lineItemTitle ?? "",
      lineItemDetail: lineItem.lineItemDescription ?? "",
      dueDate: lineItem.paidDate as Date,
      paidDate: lineItem.paidDate,
      payableNotes: lineItem.invoiceNotes ?? "",
      reimbursableExpense: lineItem.reimbursable
    };
  });
};

interface ImportPreviewProps {
  totalImportedRows: number;
  totalImportAmount: number;
}

const ImportPreview: React.FC<ImportPreviewProps> = ({
  totalImportedRows,
  totalImportAmount
}) => {
  return (
    <>
      <WSText.Heading3 mb="M">Import Preview</WSText.Heading3>
      <WSIconList
        items={[
          {
            description: `${toWSMoneyString(
              totalImportAmount
            )} of payables will be imported`,
            icon: {
              name: "coin",
              color: "blue500",
              size: "M"
            }
          },
          {
            description: `${totalImportedRows} line items will be uploaded`,
            icon: {
              name: "receipt",
              color: "blue500",
              size: "M"
            }
          }
        ]}
        mb="M"
      />
    </>
  );
};

interface ImportSummaryProps {
  totalFailedBatchItems: number;
  summary: IBulkPayableBatch | IBulkCollaboratorBatch;
  totalImportedRows: number;
  isUploadStarted?: boolean;
}

export const ImportSummary: React.FC<ImportSummaryProps> = ({
  summary,
  totalFailedBatchItems,
  totalImportedRows,
  isUploadStarted
}) => {
  let importPercent = 0;
  let progressText = "";
  if (totalFailedBatchItems > 0) {
    progressText = `${totalFailedBatchItems} of ${totalImportedRows} rows could not be processed`;
  }
  if (isUploadStarted) {
    progressText = `Uploading ${totalImportedRows} rows...`;
  }
  if (summary && !isEmpty(summary.statistics)) {
    const { statistics } = summary;
    const totalProcessed =
      Number(statistics?.itemsFailed ?? 0) +
      Number(statistics?.itemsProcessed ?? 0);
    importPercent = (totalProcessed * 100) / totalImportedRows;
    progressText = `Uploading ${totalProcessed} of ${totalImportedRows} rows...`;
    if (
      totalProcessed === totalImportedRows &&
      Number(statistics?.itemsFailed ?? 0) > 0
    ) {
      progressText = `${Number(
        statistics?.itemsFailed ?? 0
      )} out of ${totalImportedRows} rows could not be imported`;
    } else if (Number(statistics?.itemsFailed ?? 0) === 0) {
      progressText = `${Number(statistics?.itemsProcessed ?? 0)} rows imported`;
    }
  }
  return (
    <>
      <WSText.Heading4 mb="M">Import Summary</WSText.Heading4>
      <WSProgressBar percent={importPercent || 0} text={progressText} />
    </>
  );
};

enum OffPlatformPaymentsFailedNextStep {
  CorrectErrors = "CorrectErrors",
  ImportNewSpreadsheet = "ImportNewSpreadsheet",
  ImportSuccessRows = "ImportSuccessRows",
  DoNotImportPayments = "DoNotImportPayments"
}

export interface ImportFailedProps {
  importCorrectedSpreadsheet: () => void;
  importNewSpreadsheet: () => void;
  importSuccessRowOnly: () => void;
  onSkip: () => void;
  totalFailedCreateBatchItems: number;
  downloadErrorsCSV: Function;
  summary: IBulkPayableBatch | IBulkCollaboratorBatch;
  totalImportedRows: number;
}

export const ImportFailed: React.FC<ImportFailedProps> = ({
  totalFailedCreateBatchItems,
  totalImportedRows,
  summary,
  importCorrectedSpreadsheet,
  importNewSpreadsheet,
  onSkip,
  importSuccessRowOnly,
  downloadErrorsCSV
}) => {
  const history = useHistory();

  const downloadBulkPayableItemsWithErrorsCsvProps = downloadErrorsCSV();

  const onFormSubmit = async ({
    nextStep
  }: {
    nextStep: OffPlatformPaymentsFailedNextStep;
  }) => {
    if (nextStep === OffPlatformPaymentsFailedNextStep.CorrectErrors) {
      importCorrectedSpreadsheet();
    } else if (
      nextStep === OffPlatformPaymentsFailedNextStep.ImportNewSpreadsheet
    ) {
      importNewSpreadsheet();
    } else if (
      nextStep === OffPlatformPaymentsFailedNextStep.ImportSuccessRows
    ) {
      importSuccessRowOnly();
    } else if (
      nextStep === OffPlatformPaymentsFailedNextStep.DoNotImportPayments
    ) {
      onSkip();
    }
  };

  return (
    <WSPanel>
      <WSText.Heading3 mb="M">
        There was an error importing{" "}
        {summary?.statistics?.itemsFailed ?? totalFailedCreateBatchItems} rows{" "}
        of your spreadsheet.
      </WSText.Heading3>

      <ImportSummary
        summary={summary}
        totalFailedBatchItems={totalFailedCreateBatchItems}
        totalImportedRows={totalImportedRows}
      />

      <WSFlexBox.Center my="M">
        <CSVLink
          {...downloadBulkPayableItemsWithErrorsCsvProps}
          className={styles.csvLinkBtn}
        >
          <WSButton.Primary icon="download" fullWidth>
            Download CSV of errors
          </WSButton.Primary>
        </CSVLink>
      </WSFlexBox.Center>

      <WSText.Paragraph color="grey600" mb="M">
        To resolve the error, you have few options. Or, you can skip this step
        for now and import payments later from 1099-NEC Filing Dashboard.
      </WSText.Paragraph>

      <WSForm
        mb="S"
        defaultValues={{
          nextStep: OffPlatformPaymentsFailedNextStep.CorrectErrors
        }}
        onSubmit={onFormSubmit}
      >
        <WSForm.Field
          mb="L"
          name="nextStep"
          component={WSRadioInputGroup}
          componentProps={{
            options: [
              {
                value: OffPlatformPaymentsFailedNextStep.CorrectErrors,
                label: (
                  <>
                    <WSText.Heading4>
                      Correct errors (recommended)
                    </WSText.Heading4>
                    <WSText.Paragraph>
                      Download a copy of the spreadsheet, fix the errors, save,
                      and re-import.
                    </WSText.Paragraph>
                  </>
                )
              },
              {
                value: OffPlatformPaymentsFailedNextStep.ImportNewSpreadsheet,
                label: (
                  <>
                    <WSText.Heading4>Import a new spreadsheet</WSText.Heading4>
                    <WSText.Paragraph>
                      Choose a new spreadsheet without errors and import it from
                      scratch.
                    </WSText.Paragraph>
                  </>
                )
              },
              {
                disabled: totalImportedRows === totalFailedCreateBatchItems,
                value: OffPlatformPaymentsFailedNextStep.ImportSuccessRows,
                label: (
                  <>
                    <WSText.Heading4>
                      Import successfully created rows{" "}
                    </WSText.Heading4>
                    <WSText.Paragraph>
                      Preserve{" "}
                      {summary?.statistics?.itemsProcessed
                        ? `the ${summary?.statistics?.itemsProcessed}`
                        : ""}{" "}
                      successfully imported rows and skip the errors.
                    </WSText.Paragraph>
                  </>
                )
              },
              {
                value: OffPlatformPaymentsFailedNextStep.DoNotImportPayments,
                label: (
                  <>
                    <WSText.Heading4>Don't import any payments</WSText.Heading4>
                    <WSText.Paragraph>
                      You can import payments later from the 1099-NEC Filing
                      Dashboard
                    </WSText.Paragraph>
                  </>
                )
              }
            ],
            optionHasBorder: true
          }}
        />

        <WSForm.SubmitButton
          className={styles.actionBtn}
          name="chooseOffPlatformPaymentsFailureNextStep"
          fullWidth
        >
          Continue
        </WSForm.SubmitButton>
      </WSForm>
    </WSPanel>
  );
};

export interface IFailedBulkPayableItemCreate extends IBulkPayableItemCreate {
  error: string;
}

export const NEC1099FilingUploadOffPlatformPayments: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();

  const history = useHistory<{
    lineItems: UploadPayable[];
    fileName: string;
    bulkPayableBatchId: string;
    flatFileBatchId: string;
  }>();

  const {
    lineItems,
    fileName: uploadFileName,
    bulkPayableBatchId,
    flatFileBatchId
  } = history.location.state;
  const payableStatus: PayableStatus = qs.parse(
    history.location.search.substring(1)
  ).status as PayableStatus;

  const [areItemsAddedToBatch, setAreItemsAddedToBatch] = useState(false);
  const [isUploadingBatchItems, setIsUploadingBatchItems] = useState(false);
  const [isBatchUploadStarted, setIsBatchUploadStarted] = useState(false);
  const [isBatchFetchingFailed, setIsBatchFetchingFailed] = useState(false);
  const [skipErrors, setSkipErrors] = useState(false);
  const [successCreateBatchItems, setSuccessCreateBatchItems] = useState<
    IBulkPayableItem[]
  >([]);
  const [failedCreateBatchItems, setFailedCreateBatchItems] = useState<
    IFailedBulkPayableItemCreate[]
  >([]);

  const qActivity = useActivities(userId);
  const bulkPayableBatchQuery = useBulkPayableBatch(bulkPayableBatchId, {
    enabled: isBatchUploadStarted && !isBatchFetchingFailed,
    refetchInterval: 3000
  });
  const bulkPayableBatchItemsQuery = useBulkPayableBatchItems(
    bulkPayableBatchId
  );
  const bulkPayableBatchItemsNotYetProcessedQuery = useBulkPayableBatchItemsNotYetProcessed(
    bulkPayableBatchId,
    {
      refetchInterval: 3000
    }
  );

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [
    updateBulkPayableBatch,
    updateBulkPayableBatchMeta
  ] = useUpdateBulkPayableBatch();

  useEffect(() => {
    const createBulkPayableBatchItems = async () => {
      const batchItemsRequests = lineItemsToBulkBatchItems(
        lineItems,
        bulkPayableBatchId,
        payableStatus
      );
      console.log("batchItemsRequests", batchItemsRequests);
      await bulkCreateBatchItemPayable.run(batchItemsRequests);
      // TODO(@Anesh): Send statistics to analytics
    };
    if (!areItemsAddedToBatch) {
      createBulkPayableBatchItems();
    }
  }, []);

  useEffect(() => {
    // stop polling if get batch fails
    if (bulkPayableBatchQuery.isError) {
      setIsBatchFetchingFailed(true);
    }
    if (
      bulkPayableBatchQuery.data &&
      [BulkStatus.Complete, BulkStatus.Failed].includes(
        bulkPayableBatchQuery.data?.status
      )
    ) {
      setIsBatchUploadStarted(false);
    }
  }, [bulkPayableBatchQuery.data?.status]);

  useEffect(() => {
    const bulkBatchItems = bulkPayableBatchItemsQuery.data?.data || [];
    const failedBulkBatchItems = bulkBatchItems.filter(
      batchItem => batchItem.status === BulkStatus.Failed
    );
    if (failedBulkBatchItems.length > 0) {
      console.log("failedBulkBatchItems", failedBulkBatchItems);
      //TODO(@Anesh): create csv download link with failedBulkBatchItems
    }
  }, [bulkPayableBatchItemsQuery]);

  const bulkCreateBatchItemPayable = bulkActionFactory({
    onStart: () => {
      setIsUploadingBatchItems(true);
    },
    action: async (data: IBulkPayableItemCreate) => {
      try {
        const response = await paymentsService.bulkPayable.batchItem.create(
          data
        );
        setSuccessCreateBatchItems(successCreateBatchItems => [
          ...successCreateBatchItems,
          {
            ...response
          }
        ]);
        return response;
      } catch (error) {
        setFailedCreateBatchItems(failedCreateBatchItems => [
          ...failedCreateBatchItems,
          {
            ...data,
            error:
              (error as AxiosError)?.response?.data.error ||
              "Internal server error"
          }
        ]);
      }
    },
    onFinish: () => {
      setAreItemsAddedToBatch(true);
      setIsUploadingBatchItems(false);
    }
  });

  const onConfirmImport = async () => {
    await updateBulkPayableBatch(
      {
        id: bulkPayableBatchId,
        data: {
          status: BulkStatus.Pending
        }
      },
      {
        onSuccess: () => {
          // BE will start importing payables once we patch
          // BulkPayableBatch to Pending status
          setIsBatchUploadStarted(true);

          // refetch bulk batch items to set failed batch items if any
          bulkPayableBatchItemsQuery.refetch();
        }
      }
    );
  };

  const importSuccessRowOnly = () => {
    setSkipErrors(true);
    onConfirmImport();
  };

  const onContinue = async () => {
    const nec1099SetupProgress = get1099SetupProgress(qActivity.data);
    await updateActivity(
      {
        flows: {
          nec1099Setup: {
            ...nec1099SetupProgress,
            currentStep: 4
          }
        }
      },
      {
        onSuccess: () => {
          onNext();
        }
      }
    );
  };

  const bulkBatchItemsToCSVLineItems = (
    batchItems: IFailedBulkPayableItemCreate[]
  ): UploadPayable[] => {
    return batchItems.map(batchItem => {
      return {
        email: batchItem.collaboratorEmail,
        collaboratorId: batchItem.collaboratorId,
        contractorId: batchItem.collaboratorExternalId,
        amount: toWSMoneyString(batchItem.amount),
        lineItemTitle: batchItem.lineItemDescription,
        lineItemDescription: batchItem.lineItemDetail,
        dueDate: batchItem.dueDate,
        paidDate: batchItem.paidDate ?? batchItem.dueDate,
        invoiceNotes: batchItem.payableNotes,
        reimbursable: batchItem.reimbursableExpense,
        error: batchItem.error
      };
    });
  };

  const downloadErrorsCSV = () => {
    return {
      filename: `Wingspan_Off_Platform_Payments_Failed.csv`,
      headers: [
        { label: "Collaborator ID", key: "collaboratorId" },
        { label: "External Collaborator ID", key: "contractorId" },
        { label: "Email", key: "email" },
        { label: "Line Item Title", key: "lineItemTitle" },
        { label: "Line Item Description", key: "lineItemDescription" },
        { label: "Amount", key: "amount" },
        { label: "Pay Date", key: "paidDate" },
        { label: "Reimbursable", key: "reimbursable" },
        { label: "Error", key: "error" }
      ],
      data: bulkBatchItemsToCSVLineItems(failedCreateBatchItems)
    };
  };

  const getTotalImportAmount = (batchItems: IBulkPayableItem[]) => {
    return batchItems.reduce((total, batchItem) => {
      return total + batchItem.amount;
    }, 0);
  };

  let totalImportedRows = lineItems.length;
  let totalImportAmount = getTotalLineItemsAmountFromCSV(lineItems);
  const bulkPayableBatch = bulkPayableBatchQuery.data as IBulkPayableBatch;

  if (skipErrors) {
    totalImportedRows = successCreateBatchItems.length;
    totalImportAmount = getTotalImportAmount(successCreateBatchItems);
  }

  const createBatchItemsProgress =
    successCreateBatchItems.length + failedCreateBatchItems.length;

  const totalRemaining =
    bulkPayableBatchItemsNotYetProcessedQuery.data?.summary.listSize || 0;

  return (
    <NEC1099FilingContainer title="Off-platform payments">
      <PreventLeave
        isEnabled={
          successCreateBatchItems.length > 0 ||
          failedCreateBatchItems.length > 0
        }
        promptMessage="Your payables have already been uploaded and are getting processed to be imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
      />
      {isUploadingBatchItems ? (
        // Show spinner when Bulk Payable Items are adding to Bulk Batch
        <WSPanel>
          <WSFlexBox.Center>
            <WSLoader.Spinner mb="M" />
          </WSFlexBox.Center>
          <WSFlexBox.Center>
            <WSText ml="S" color="grey700">
              Processing {createBatchItemsProgress} of {totalImportedRows} row
              {totalImportedRows > 1 ? "s" : ""} in spreadsheet...
            </WSText>
          </WSFlexBox.Center>
        </WSPanel>
      ) : failedCreateBatchItems.length > 0 && skipErrors === false ? (
        // handle error view for batch items which failed to add to bulk batch
        <ImportFailed
          downloadErrorsCSV={downloadErrorsCSV}
          totalImportedRows={totalImportedRows}
          totalFailedCreateBatchItems={failedCreateBatchItems.length}
          summary={bulkPayableBatch}
          importCorrectedSpreadsheet={() => {
            history.push(
              `/member/1099-filing/set-up/upload-failed-off-platform-payments/batch/${bulkPayableBatchId}?status=Paid`
            );
          }}
          importNewSpreadsheet={() => {
            history.push("/member/1099-filing/set-up/off-platform-payments");
          }}
          importSuccessRowOnly={importSuccessRowOnly}
          onSkip={onNext}
        />
      ) : (
        // success case - show import preview
        <WSPanel>
          <ImportPreview
            totalImportedRows={totalImportedRows}
            totalImportAmount={totalImportAmount}
          />

          <ImportSummary
            summary={bulkPayableBatch}
            totalFailedBatchItems={failedCreateBatchItems.length}
            totalImportedRows={totalImportedRows - totalRemaining}
            isUploadStarted={isBatchUploadStarted}
          />

          {isBatchUploadStarted ||
          bulkPayableBatchQuery.data?.status === BulkStatus.Complete ? (
            <WSButton.Primary
              mt="L"
              className={styles.actionBtn}
              fullWidth
              name="continueToCalculationSettings"
              onClick={onContinue}
              loading={updateActivityMeta.isLoading}
              disabled={isBatchUploadStarted}
            >
              Continue to 1099-NEC calculation
            </WSButton.Primary>
          ) : (
            <WSButton.Primary
              mt="L"
              className={styles.actionBtn}
              fullWidth
              name="continueToCalculationSettings"
              onClick={onConfirmImport}
              loading={
                isBatchUploadStarted || updateBulkPayableBatchMeta.isLoading
              }
            >
              Upload
            </WSButton.Primary>
          )}
        </WSPanel>
      )}
    </NEC1099FilingContainer>
  );
};
